import { useState, useEffect, useMemo } from 'react';
import ReviewCard from '../components/ReviewCard';
import { fetchCompanyBySlug } from '../api';
import Loader from '../components/Loader';

const FILTERS = ['All', '👻 Ghosted', '✅ Received', '❌ Rejected'];

export default function CompanyDetailPage({ companyName, initialFilter = 'All', onFilterChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(initialFilter);

  useEffect(() => {
    async function loadCompanyDetails() {
      setLoading(true);
      try {
        const slug = companyName.toLowerCase().replace(/\s+/g, "-");
        const result = await fetchCompanyBySlug(slug);
        setData(result);
      } catch (err) {
        console.error('Failed to load company details:', err);
      } finally {
        setLoading(false);
      }
    }
    if (companyName) loadCompanyDetails();
  }, [companyName]);

  const filteredReviews = useMemo(() => {
    if (!data || !data.reviews) return [];
    if (activeFilter === 'All') return data.reviews;
    if (activeFilter === '👻 Ghosted') return data.reviews.filter(r => r.status === 'ghosted');
    if (activeFilter === '✅ Received') return data.reviews.filter(r => r.status === 'offer');
    if (activeFilter === '❌ Rejected') return data.reviews.filter(r => r.status === 'rejected');
    return data.reviews;
  }, [data, activeFilter]);

  const handleFilterClick = (f) => {
    setActiveFilter(f);
    if (onFilterChange) onFilterChange(f);
  };

  if (loading) return <Loader message="Polishing details..." />;
  if (!data || !data.company) return <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>Company not found.</div>;

  const { company } = data;

  // Compute derived stats from raw Firestore fields (backend /:slug doesn't compute these)
  const ghostPercent = company.totalReviews > 0 ? Math.round((company.ghostedCount / company.totalReviews) * 100) : (company.ghostPercent || 0);
  const offerRate = company.totalReviews > 0 ? Math.round((company.offerCount / company.totalReviews) * 100) : (company.offerRate || 0);
  const avgDaysToOffer = company.countWithTimeline > 0 ? +(company.totalDaysToOffer / company.countWithTimeline).toFixed(1) : (company.avgDaysToOffer || 0);

  return (
    <div style={{ maxWidth: '100%', margin: '0 auto', padding: '16px 0', animation: 'fadeIn 0.4s ease-out' }}>
      <div className="company-detail-header" style={{
        background: '#fff',
        border: '1px solid var(--border)',
        borderRadius: 20,
        padding: '32px',
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 28,
        boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
      }}>
        <div className="company-detail-logo" style={{
          width: 80, height: 80, borderRadius: 20, background: 'linear-gradient(135deg, var(--blue), #0284c7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, color: '#fff',
          fontWeight: 900, boxShadow: '0 8px 16px rgba(14,165,233,0.2)'
        }}>
          {company.name?.charAt(0)}
        </div>
        <div style={{ flex: 1 }}>
          <h1 className="company-detail-title" style={{ fontFamily: 'var(--display)', fontSize: 32, margin: '0 0 8px', fontWeight: 900, color: 'var(--text)', letterSpacing: -1 }}>{company.name}</h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            <Badge icon="🌟" label={`${(company.avgRatingOverall || 0).toFixed(1)} Score`} color="var(--gold)" />
            <Badge icon="✅" label={`${offerRate}% Offer Rate`} color="var(--green)" />
            <Badge icon="👻" label={`${ghostPercent}% Ghost Rate`} color="#ef4444" />
            <Badge icon="⏱️" label={avgDaysToOffer > 0 ? `${avgDaysToOffer}d Avg Speed` : 'Speed N/A'} color="var(--blue)" />
            <Badge icon="📊" label={`${company.totalReviews || 0} Kahanis`} color="var(--text2)" />
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
          <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0, color: 'var(--text)', letterSpacing: -0.5 }}>
            Community Kahanis ({filteredReviews.length})
          </h3>
          <div className="company-detail-filters-row" style={{ display: 'flex', gap: 6 }}>
            {FILTERS.map(f => (
              <button
                key={f}
                onClick={() => handleFilterClick(f)}
                style={{
                  padding: '6px 14px', borderRadius: 100, fontSize: 13,
                  border: `1px solid ${activeFilter === f ? 'var(--blue)' : 'var(--border)'}`,
                  background: activeFilter === f ? 'var(--blue)' : '#fff',
                  color: activeFilter === f ? '#fff' : 'var(--text2)',
                  cursor: 'pointer', transition: 'all 0.2s', fontWeight: 700
                }}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {filteredReviews.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)', background: 'var(--surface2)', borderRadius: 16, border: '2px dashed var(--border)' }}>
            <div style={{ fontSize: 42, marginBottom: 12 }}>🔍</div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>No matching reports found</div>
            <div style={{ fontSize: 14, marginTop: 4 }}>Try changing the filter to see more kahanis.</div>
          </div>
        ) : (
          filteredReviews.map((rev, idx) => (
            <ReviewCard key={rev.id || idx} review={rev} delay={idx * 0.05} />
          ))
        )}
      </div>
    </div>
  );
}

function Badge({ icon, label, color }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 8,
      background: 'var(--surface2)', fontSize: 13, fontWeight: 700, color: 'var(--text2)',
      border: '1px solid var(--border)'
    }}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span style={{ color }}>{label}</span>
    </div>
  );
}
